import React, { useEffect, useCallback } from 'react';
import {
  Container,
  Stack,
  Typography,
  Button,
  Grid,
  Card,
  Box,
  Divider,
  Paper
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import CalculateIcon from '@mui/icons-material/Calculate';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useReceiveSelector from '../../store/Slice/Receive/ReceiveSelector';
import { getReceiveRecordData } from '../../store/Slice/Receive/ReceiveSlice';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { fDate2 } from '../../utils/formatTime';
import {
  getBankBranchData,
  getBankDropdownData,
  getLoanTypeData,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';
import { ReceiveApi } from '../../utils/Apis';

function ViewFile() {
  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      grossInvoiceValue: '',
      deduction: '',
      ownerName: '',
      adjustedInvoiceValue: '',
      netInvoiceValue: '',
      commissionNetPayableAmount: ''
    }
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId } = location?.state || {};
  const { LoginData } = useLoginSelector();
  const { ReceiveRecordData } = useReceiveSelector();
  const { LoanTypeDrop, BankBranchDrop, BankDrop } = useGlobalSelector();

  const matchStatusOptions = [
    { label: 'Matched', value: true },
    { label: 'Not Matched', value: false }
  ];

  const getReceiveRecordApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      filterModel: {
        bankId: rowId?.bankId || null,
        branchId: rowId?.branchId || null,
        loanTypeId: rowId?.loanTypeId || null,
        misIsMatch: rowId?.misIsMatch || null,
        fromDate: null,
        toDate: null,
        fileNumber: rowId?.fileNumber || '',
        receivableId: rowId?.receivableId || ''
      }
    };
    dispatch(getReceiveRecordData(credentials));
  }, [rowId, LoginData, dispatch]);

  useEffect(() => {
    getReceiveRecordApi();
  }, [getReceiveRecordApi]);

  useEffect(() => {
    if (ReceiveRecordData) {
      const myData = ReceiveRecordData?.[0];
      setValue('bankId', myData?.bankId);
      setValue('borrowerName', myData?.borrowerName);
      setValue('branchId', myData?.branchId);
      setValue('branchName', myData?.branchName);
      setValue('empClass', myData?.empClass);
      const rawDisbursed = myData?.fileDisbursedDate;
      const safeDisbursed =
        rawDisbursed && String(rawDisbursed).toLowerCase() !== 'null' ? fDate2(rawDisbursed) : '';
      setValue('fileDisbursedDate', safeDisbursed);
      setValue('fileNumber', myData?.fileNumber);
      setValue('invoiceNo', myData?.invoiceNo);
      setValue('loanAmountDisbursed', myData?.loanAmountDisbursed);
      setValue('loanTypeId', myData?.loanTypeId);
      setValue('misIsMatch', myData?.misIsMatch);
      setValue('misMonth', myData?.misMonth);
      setValue('receivableDeduction', myData?.receivableDeduction);
      setValue('receivableId', myData?.receivableId);
      setValue('revenueId', myData?.revenueId);

      // second card
      setValue('netInvoiceValue', myData?.receivableInvoice?.netInvoiceValue);
      setValue('grossInvoiceValue', myData?.receivableInvoice?.grossInvoiceValue);
      setValue('deduction', myData?.receivableInvoice?.deduction);
      setValue('adjustedInvoiceValue', myData?.receivableInvoice?.adjustedInvoiceValue);
      setValue('totalGst', myData?.receivableInvoice?.totalGst || 18);

      setValue('receivableInvoiceId', myData?.receivableInvoicePayments?.receivableInvoiceId);

      // Nested object fields
      setValue('ownerName', myData?.receivableInvoicePayments?.ownerName);
      setValue('commissionDeduction', myData?.receivableInvoicePayments?.commissionDeduction);
      setValue(
        'commissionNetPayableAmount',
        myData?.receivableInvoicePayments?.commissionNetPayableAmount
      );
      setValue(
        'receivableInvoicePaymentId',
        myData?.receivableInvoicePayments?.receivableInvoicePaymentId
      );
    }
  }, [ReceiveRecordData, setValue]);

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,

        bankId: rowId?.bankId || null,
        fileNumber: rowId?.fileNumber || '',
        receivableId: rowId?.receivableId || '',
        receivableDeduction: {
          receivableDeductionId: null,
          receivableId: null,
          totalProcessingFee: null,
          bankWaiver: null,
          applicantShare: null,
          companyShare: null,
          processfeeStatusId: null,
          feeDeductedFromInvoice: null
        },
        receivableInvoice: {
          receivableInvoiceId: ReceiveRecordData?.receivableInvoice?.receivableInvoiceId,
          invoiceNumber: ReceiveRecordData?.receivableInvoice?.invoiceNumber,
          grossInvoiceValue: data?.grossInvoiceValue,
          deduction: data?.deduction,
          adjustedInvoiceValue: data?.adjustedInvoiceValue,
          totalGst: ReceiveRecordData?.receivableInvoice?.totalGst || 18,
          netInvoiceValue: data?.netInvoiceValue
        },
        receivableInvoicePayments: {
          receivableInvoicePaymentId:
            ReceiveRecordData?.receivableInvoice?.receivableInvoicePaymentId,
          commissionNetPayableAmount: data?.commissionNetPayableAmount,
          commissionDeduction: data?.commissionDeduction,
          ownerName: data?.ownerName
        }
      };

      try {
        const res = await dispatch(saveApiData(ReceiveApi?.receiveSave, finalData));
        if (!res) return;
        if (res?.status === '0') {
          dispatch(setApiSuccess('Receivable details saved successfully'));
          navigate('/receivable');
        } else {
          dispatch(setApiError(res?.msg || 'Something went wrong'));
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        dispatch(setApiError('Something went wrong'));
      }
    },
    [LoginData, rowId, ReceiveRecordData, dispatch, navigate]
  );

  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData, dispatch]);

  const handleBranchDetails = useCallback(() => {
    const myData = ReceiveRecordData?.[0]?.bankId;
    if (!myData) return;
    dispatch(getBankBranchData(myData));
  }, [ReceiveRecordData, dispatch]);

  useEffect(() => {
    handleBranchDetails();
  }, [handleBranchDetails]);

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  return (
    <Page title="Partner Management System || View Receivable File">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
            sx={{ mb: 3 }}
          >
            <PageTitle info="View Receivable File" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <form id="addPayableFile" onSubmit={handleReduxSubmit(onSubmit)}>
            <Stack spacing={3}>
              {/* Card 1: File Details */}
              <Card
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: 'rgba(19, 81, 157, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#13519d'
                    }}
                  >
                    <DescriptionIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                    File Details
                  </Typography>
                </Stack>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="bankId"
                      label="Bank Name"
                      control={control}
                      errors={errors}
                      options={BankDrop || []}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="fileNumber"
                      label="File No"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="borrowerName"
                      label="Borrower Name"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="branchId"
                      label="Branch Name"
                      control={control}
                      errors={errors}
                      options={BankBranchDrop || []}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="empClass"
                      label="Employee Class"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="fileDisbursedDate"
                      label="Disbursed Date"
                      type="date"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="loanAmountDisbursed"
                      label="Disbursed Amount"
                      type="Number"
                      isAmount
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="loanTypeId"
                      label="Loan Product Type"
                      control={control}
                      errors={errors}
                      options={LoanTypeDrop || []}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="misMonth"
                      label="MIS Month"
                      type="month"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="misIsMatch"
                      label="MIS Status"
                      control={control}
                      errors={errors}
                      options={matchStatusOptions || []}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Card 2: Invoice Calculation */}
              <Card
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: 'rgba(19, 81, 157, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#13519d'
                    }}
                  >
                    <CalculateIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                    Invoice Calculation
                  </Typography>
                </Stack>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="grossInvoiceValue"
                      label="Gross Invoice Value"
                      type="Number"
                      isAmount
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="deduction"
                      label="Deduction"
                      type="Number"
                      isAmount
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="adjustedInvoiceValue"
                      label="Adjusted Invoice Value"
                      type="Number"
                      isAmount
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="totalGst"
                      label="GST %"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="netInvoiceValue"
                      label="Net Invoice Value"
                      type="Number"
                      isAmount
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Card 3: Partner Payout Details */}
              <Card
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: 'rgba(19, 81, 157, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#13519d'
                    }}
                  >
                    <AccountBalanceWalletIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                    Partner Payout Details
                  </Typography>
                </Stack>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="ownerName"
                      label="Partner Name"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="NAME"
                      fieldType="ALPHABET"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="commissionDeduction"
                      label="Applicable Commission %"
                      type="Number"
                      control={control}
                      errors={errors}
                      disabled={
                        ReceiveRecordData?.receivableInvoice?.adjustedInvoiceValue === 'null'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="commissionNetPayableAmount"
                      label="Payout Amount"
                      type="Number"
                      isAmount
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Bottom Action Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/receivable')}
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#64748b',
                    borderColor: '#cbd5e1',
                    '&:hover': { bgcolor: '#f1f5f9' }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="addPayableFile"
                  variant="contained"
                  sx={{
                    borderRadius: '8px',
                    px: 4,
                    py: 1,
                    bgcolor: '#13519d',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(19, 81, 157, 0.25)',
                    '&:hover': { bgcolor: '#0d3d75' }
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Container>
    </Page>
  );
}

export default ViewFile;
